import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Badge from "@components/Badge/Badge";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import StepperControls from "@components/StepperControls/StepperControls.tsx";
import StepperTabPanel from "@components/StepperTabs/StepperTabPanel.tsx";
import StepperTabs from "@components/StepperTabs/StepperTabs.tsx";
import {
  STEPPER_TYPE,
  StepStatus,
  type StepperTabsHandle,
  type StepStatusValue,
} from "@components/StepperTabs/StepperTabs.types.ts";
import { useStepStatuses } from "@components/StepperTabs/useStepStatuses";
import Subheader from "@components/Subheader/Subheader.tsx";
import {
  CertificatesContexts,
  getVesselCertificates,
  VesselTypeCodes,
  VRCertificateTypes,
  type VesselTypeCode,
  type VRCertificateType,
} from "@constants/certificates";
import { useNotification } from "@context/NotificationContext.tsx";
import type { FormContext } from "@custom-types/form-types";
import { steps } from "@features/vessel-register/components/utils.ts";
import { useVesselRegistration } from "@features/vessel-register/hooks/useVesselRegistration.tsx";
import type { VesselRegisterFormData } from "@features/vessel-register/schema/vesselRegisterFormSchema.ts";
import { getVesselRegisterFormSchema } from "@features/vessel-register/schema/vesselRegisterFormSchema.ts";
import {
  defaultValues as emptyFormDefaults,
  useVesselRegisterFormStore,
} from "@features/vessel-register/store/useVesselRegisterForm.store.ts";
import { usePersistentForm } from "@hooks/usePersistentForm.ts";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import type { Vessel, VesselsResponse } from "@services/vessel/types";
import { getVessels } from "@services/vesselsApi";
import { useQuery } from "@tanstack/react-query";
import { runFieldNavigation } from "@telemetry/runFieldNavigation";
import { runInquiry } from "@telemetry/runInquiry";
import { hasFormData } from "@utils/form";
import { paths } from "@utils/navigation";
import { checkPermission } from "@utils/user-permissions";
import type { FieldPath, FieldPathValue, FieldValues, SubmitErrorHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { generatePath, Outlet, useLocation, useNavigate } from "react-router-dom";
import { FORM_FIELDS, generateCertificateField } from "../utils";
import { useUploadAdditionalDocuments } from "./AdditionalDocuments/useUploadAdditionalDocument";
import { STEP_FIELDS } from "./constants";
import { useUploadCertificate } from "./VesselCertificates/useUploadCertificate";
import VesselsByNameDialog from "./VesselInformation/VesselsByNameDialog";

const LANG_BASE = "vessel-management";
const TEST_ID_BASE = "vessels-register-page";
const VESSEL_STATUS = {
  APPROVED: "APPROVED",
  PENDING_APPROVAL: "PENDING_APPROVAL",
  REJECTED: "REJECTED",
  DRAFT: "DRAFT",
};
const VESSEL_NAME_CHECK_INQUIRY_CONTEXT = {
  module: "vessel_registration",
  screen: "Vessel Registration",
  action: "check_vessel_name_duplicates",
  entityType: "vessel",
};
const VESSEL_NAME_CHECK_FIELD_NAVIGATION_CONTEXT = {
  module: "vessel_registration",
  screen: "Vessel Registration",
  action: "validate_vessel_name_before_next_step",
  fromField: "vesselName",
  toField: "vesselNameDuplicateMatches",
  entityType: "vessel",
};

export type VesselOutletContext = FormContext<VesselRegisterFormData> & {
  cancel: () => void;
  isRestrictedToCertificatesUpdate: boolean;
  vesselDetails?: Vessel;
  isUpdateMode: boolean;
};

type VRFormData = VesselRegisterFormData & FieldValues;

const buildCertificateKeys = (certs: VRCertificateType[]): string[] => {
  const keys: string[] = [];
  for (const cert of Object.values(certs)) {
    keys.push(
      generateCertificateField.url(cert),
      generateCertificateField.documentName(cert),
      generateCertificateField.className(cert),
      generateCertificateField.expiryDate(cert),
      generateCertificateField.permanent(cert),
    );
  }
  return keys;
};

const VesselRegistrationContainer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { notify } = useNotification();

  const [touchedSteps, setTouchedSteps] = useState<Record<number, boolean>>(
    Object.keys(STEP_FIELDS).reduce(
      (acc, key) => {
        acc[Number(key)] = false;
        return acc;
      },
      {} as Record<number, boolean>,
    ),
  );

  const { setField, setAll, resetForm } = useVesselRegisterFormStore();
  const stepperTabsRef = useRef<StepperTabsHandle>(null);

  const [submitPending, setSubmitPending] = useState(false);
  const [savePending, setSavePending] = useState(false);

  const storeSnapshot = useVesselRegisterFormStore.getState();
  const formDefaults: VesselRegisterFormData = storeSnapshot;

  const existingVesselDetails = (location.state as { vesselDetails?: Vessel })?.vesselDetails;
  const existingVesselId = existingVesselDetails?.id;
  const vesselHeader = `${existingVesselDetails?.vesselImoNumber || "/"} - ${existingVesselDetails?.vesselName || "/"}`;

  const isEditMode = !!existingVesselDetails;
  const isUpdateMode =
    isEditMode && existingVesselDetails?.vesselRegistrationStatus === VESSEL_STATUS.APPROVED;
  const isRestrictedToCertificatesUpdate = Boolean(
    existingVesselDetails?.restrictedToCertificatesUpdate,
  );

  const { userAccessPermissions } = useAuthStore();
  const isMarineDepartment = checkPermission(userAccessPermissions, "hasTaskAccess");

  const schema = useMemo(
    () =>
      getVesselRegisterFormSchema(
        t,
        existingVesselId,
        isMarineDepartment,
        false,
        isRestrictedToCertificatesUpdate,
        isUpdateMode && existingVesselDetails?.bunkerRegistered,
      ),
    [t, existingVesselId, isMarineDepartment, isRestrictedToCertificatesUpdate],
  );
  const draftSchema = useMemo(
    () => getVesselRegisterFormSchema(t, existingVesselId, isMarineDepartment, true),
    [t, existingVesselId, isMarineDepartment],
  );

  const form = usePersistentForm<VesselRegisterFormData>({
    schema,
    defaultValues: formDefaults,
    storeValues: formDefaults,
    setStoreValues: setAll,
    resetStore: resetForm,
    persistKey: "vessel-registration-form",
    dateKeys: [
      ...Object.values(VRCertificateTypes).flatMap((cert) => [
        generateCertificateField.expiryDate(cert),
      ]),
      FORM_FIELDS.NAVIGATION_LICENSE_FROM_DATE,
      FORM_FIELDS.NAVIGATION_LICENSE_TO_DATE,
      FORM_FIELDS.CHARTER_START_DATE,
      FORM_FIELDS.CHARTER_END_DATE,
      FORM_FIELDS.OIL_SPILL_COMPANY_FROM_DATE,
      FORM_FIELDS.OIL_SPILL_COMPANY_TO_DATE,
      FORM_FIELDS.SIRE_REPORT_FROM_DATE,
      FORM_FIELDS.SIRE_REPORT_TO_DATE,
      FORM_FIELDS.HOSE_TEST_CERTIFICATE_EXPIRY_DATE,
      FORM_FIELDS.SHIP_TO_SHIP_PLAN_FROM_DATE,
      FORM_FIELDS.SHIP_TO_SHIP_PLAN_TO_DATE,
      FORM_FIELDS.EFFECTIVE_START_DATE,
      FORM_FIELDS.EFFECTIVE_END_DATE,
    ],
  });
  const { reset, clearErrors, watch, setError } = form;

  const isBunkerBarge = form.watch(FORM_FIELDS.BUNKER_REGISTERED);
  const hasUploadStorePending = useUploadStore((state) =>
    Object.values(state.uploadsInFlightByKey).some((count) => count > 0),
  );

  const { isPending: isAdditionalDocumentsPending } = useUploadAdditionalDocuments({});
  const { isPending: isCertificatePending } = useUploadCertificate();

  const uploadsPending = useMemo(
    () => isAdditionalDocumentsPending || isCertificatePending || hasUploadStorePending,
    [hasUploadStorePending, isAdditionalDocumentsPending, isCertificatePending],
  );

  const currentStepIndex = steps(isBunkerBarge).findIndex((step) =>
    location.pathname.endsWith(step.path),
  );
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === (isBunkerBarge ? 5 : 4);

  const [nameCheckDialogOpen, setNameCheckDialogOpen] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const vesselTypeCode = watch(FORM_FIELDS.VESSEL_TYPE_CODE);
  const grossTonnage = watch(FORM_FIELDS.GROSS_TONNAGE);

  const listOfCertificates: VRCertificateType[] = useMemo(() => {
    if (!vesselTypeCode) return [];

    return getVesselCertificates(
      CertificatesContexts.BASE,
      vesselTypeCode as VesselTypeCode,
      grossTonnage,
    ).map((cert) => cert.type);
  }, [vesselTypeCode, grossTonnage]);

  const certKeys = useMemo(() => buildCertificateKeys(listOfCertificates), [listOfCertificates]);

  const stepFields: Record<number, string[]> = useMemo(
    () => ({
      ...STEP_FIELDS,
      3: certKeys,
    }),
    [certKeys],
  );

  const { statusFor } = useStepStatuses<VRFormData>({
    schema,
    form,
    stepFields,
    touchedSteps,
    currentStepIndex,
  });

  const getStatusForStep = (index: number): StepStatusValue => {
    const currentStepIndex = stepperTabsRef.current?.getActiveStepperTab();
    if (currentStepIndex === index) return StepStatus.CURRENT;
    if (index === 3 && isMarineDepartment && touchedSteps[3]) return StepStatus.COMPLETE;
    if (index === 3 && vesselTypeCode === VesselTypeCodes.NAVY_VESSELS) return StepStatus.COMPLETE;

    return statusFor(index);
  };

  const updateFormData: VesselOutletContext["updateFormData"] = (key, value) => {
    setField(
      key as FieldPath<VesselRegisterFormData>,
      value as FieldPathValue<VesselRegisterFormData, FieldPath<VesselRegisterFormData>>,
    );
  };

  // clear uploaded files from the upload store on form reset or submission
  const clearStoredFiles = useCallback(() => {
    const uploadStore = useUploadStore.getState();
    Object.values(VRCertificateTypes).forEach((certificateKey) => {
      uploadStore.clearFilesForKey(certificateKey);
    });
    uploadStore.clearFilesForKey(FORM_FIELDS.ADDITIONAL_DOCUMENTS);
    uploadStore.clearFilesForKey(FORM_FIELDS.NAVIGATION_LICENSE);
    uploadStore.clearFilesForKey(FORM_FIELDS.CHARTER_PARTY_AGREEMENT);
    uploadStore.clearFilesForKey(FORM_FIELDS.OIL_SPILL_COMPANY);
    uploadStore.clearFilesForKey(FORM_FIELDS.SIRE_REPORT);
    uploadStore.clearFilesForKey(FORM_FIELDS.HOSE_TEST_CERTIFICATE);
    uploadStore.clearFilesForKey(FORM_FIELDS.SHIP_TO_SHIP_PLAN);
    uploadStore.clearFilesForKey(FORM_FIELDS.BUNKER_BARGE_ADDITIONAL_DOCUMENTS);
  }, []);

  const clearForm = () => {
    clearStoredFiles();
    resetForm();
    reset();
  };

  const clearFormUpdate = useCallback(() => {
    resetForm();
    reset();
  }, [resetForm, reset]);

  const cancel = () => {
    clearForm();
    if (existingVesselDetails?.id) {
      navigate(
        generatePath(`/${paths.vesselManagement.index}/${paths.vesselManagement.vesselStatus}`, {
          id: existingVesselDetails.id,
        }),
      );
    } else {
      navigate(`/${paths.vesselManagement.index}`);
    }
  };

  const goToNextStep = () => {
    const nextStep = steps(isBunkerBarge)[currentStepIndex + 1];
    if (nextStep) {
      stepperTabsRef.current?.next();
    }
  };

  const goToPreviousStep = () => {
    const prevStep = steps(isBunkerBarge)[currentStepIndex - 1];
    if (prevStep) {
      stepperTabsRef.current?.back();
    }
  };

  const shouldShowSaveDraft =
    !isMarineDepartment &&
    (!isEditMode || existingVesselDetails?.markedAsDraft) &&
    hasFormData(form.getValues(), emptyFormDefaults);

  const notifyUploadsPending = useCallback(() => {
    notify({
      title: t("common.notification.please-wait-for-upload"),
      type: "warning",
    });
  }, [notify, t]);

  const onValid = async () => {
    if (uploadsPending) {
      notifyUploadsPending();
      return;
    }
    setSubmitPending(true);
    await onSubmit({ isMarineDepartment });
  };

  const notifyInvalid = (isDraft = false) => {
    if (isDraft) {
      notify({
        title: t(`${LANG_BASE}.notifications.vessel-draft-saved-error-title`),
        description: t(`${LANG_BASE}.notifications.vessel-draft-saved-error-description`),
        type: "error",
      });
      return;
    }
    notify({
      title: t(`${LANG_BASE}.notifications.vessel-submitted-error-title`),
      description: t(`${LANG_BASE}.notifications.vessel-submitted-error-description`),
      type: "error",
    });
  };

  const onInvalid: SubmitErrorHandler<VesselRegisterFormData> = () => {
    notifyInvalid();
  };

  const { onSave, onSubmit } = useVesselRegistration({
    formValues: form.getValues(),
    resetForm: clearForm,
    onSubmitError: () => setSubmitPending(false),
    onSaveError: () => setSavePending(false),
  });

  const complete = () => {
    setTouchedSteps(
      Object.keys(STEP_FIELDS).reduce(
        (acc, key) => {
          acc[Number(key)] = true;
          return acc;
        },
        {} as Record<number, boolean>,
      ),
    );
    if (uploadsPending) {
      notifyUploadsPending();
      return;
    }

    form.handleSubmit(onValid, onInvalid)();
  };

  const setFormError = setError as unknown as (
    name: string,
    error: { type: "manual"; message: string },
  ) => void;

  const applyZodErrors = (issues: { path: (string | number)[]; message: string }[]) => {
    for (const issue of issues) {
      if (!issue.path.length) continue;
      const name = issue.path.join(".");
      setFormError(name, { type: "manual", message: issue.message });
    }
  };

  const handleSave = async () => {
    clearErrors();
    if (uploadsPending) {
      notifyUploadsPending();
      return;
    }

    const values = form.getValues();
    const result = await draftSchema.safeParseAsync(values);

    if (!result.success) {
      applyZodErrors(result.error.issues);
      notifyInvalid(true);
      return;
    }

    setSavePending(true);
    await onSave();
  };

  const outletCtx: VesselOutletContext = {
    vesselDetails: existingVesselDetails,
    formData: form,
    updateFormData: updateFormData,
    clearErrors: clearErrors,
    watch: watch,
    setError: setError,
    cancel,
    isRestrictedToCertificatesUpdate,
    isUpdateMode,
  };

  const vesselName = form.watch(FORM_FIELDS.VESSEL_NAME);
  const noImoNumber = form.watch(FORM_FIELDS.NO_IMO_NUMBER);

  const {
    data: { vessels = [], totalRecordCount = 0 } = {},
    isLoading,
    refetch,
  } = useQuery<VesselsResponse>({
    queryKey: ["vessels", vesselName, pageNumber, pageSize],
    queryFn: () => getVessels("bff-vessel", { pageSize, pageNumber, vesselName }),
    enabled: false,
  });

  const refetchVesselNameMatches = useCallback(async () => {
    const result = await refetch();
    if (result.isError) {
      throw result.error ?? new Error("Vessel name check failed");
    }
    return result;
  }, [refetch]);

  useEffect(() => {
    if (nameCheckDialogOpen) {
      void runInquiry(VESSEL_NAME_CHECK_INQUIRY_CONTEXT, refetchVesselNameMatches).catch(
        () => undefined,
      );
    }
  }, [pageSize, pageNumber]);

  const handleOnStepperTabChange = useCallback(
    async (index: number) => {
      setTouchedSteps((prev) => ({ ...prev, [currentStepIndex]: true }));
      if (
        index !== 0 &&
        currentStepIndex === 0 &&
        vesselName &&
        noImoNumber &&
        vesselName !== existingVesselDetails?.vesselName
      ) {
        let vesselMatches: Vessel[] | undefined;
        try {
          const { data } = await runFieldNavigation(
            VESSEL_NAME_CHECK_FIELD_NAVIGATION_CONTEXT,
            () => runInquiry(VESSEL_NAME_CHECK_INQUIRY_CONTEXT, refetchVesselNameMatches),
          );
          vesselMatches = data?.vessels;
        } catch {
          vesselMatches = undefined;
        }
        if (vesselMatches && vesselMatches.length) {
          setNameCheckDialogOpen(true);
        }
      }
      const path = steps(isBunkerBarge)[index].path;
      navigate(path, { state: location.state });
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [
      currentStepIndex,
      vesselName,
      noImoNumber,
      location,
      vessels,
      isBunkerBarge,
      existingVesselDetails?.vesselName,
      refetchVesselNameMatches,
    ],
  );

  useEffect(() => {
    if (!isEditMode && !form.getValues(FORM_FIELDS.VESSEL_TYPE_CODE)) {
      clearStoredFiles();
      return;
    }

    if (!isEditMode) return;

    return () => {
      clearFormUpdate();
    };
  }, [clearFormUpdate, isEditMode]);

  return (
    <div data-testid={TEST_ID_BASE}>
      <Subheader
        title={
          <div className="flex items-end justify-between w-full">
            <div>
              <span>
                {existingVesselDetails ? vesselHeader : t(`${LANG_BASE}.register-vessel`)}
              </span>
              {existingVesselDetails && (
                <div className="flex items-center min-h-10">
                  <div className="w-40">
                    <Badge
                      type={
                        existingVesselDetails.vesselRegistrationStatus === VESSEL_STATUS.APPROVED
                          ? "success"
                          : existingVesselDetails.vesselRegistrationStatus ===
                              VESSEL_STATUS.REJECTED
                            ? "error"
                            : existingVesselDetails.vesselRegistrationStatus === VESSEL_STATUS.DRAFT
                              ? "neutral"
                              : "warning"
                      }
                      label={
                        existingVesselDetails.vesselRegistrationStatus
                          ? t(
                              `vessel-management.status-labels.${existingVesselDetails.vesselRegistrationStatus}`,
                            )
                          : t("common.unknown")
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        }
        subtitle={t(`${LANG_BASE}.vessel-registration`)}
      />
      <div className="pt-8 pb-8 px-24">
        <StepperTabs
          ref={stepperTabsRef}
          testId="form-stepper-tabs"
          onStepperTabChange={handleOnStepperTabChange}
          currentStepIndex={currentStepIndex}
          stepperType={STEPPER_TYPE.VERTICAL}
          isTypeChangeable={false}
        >
          {steps(isBunkerBarge).map((step, index) => (
            <StepperTabPanel
              key={step.path}
              label={t(`${step.label}`)}
              status={getStatusForStep(index)}
            >
              <div className="min-w-full flex flex-col bg-white">
                <Outlet context={outletCtx} />
                <div className="pb-6 ">
                  <StepperControls
                    prev={!isFirstStep ? goToPreviousStep : undefined}
                    next={isLastStep ? undefined : goToNextStep}
                    backLabelTranslationKey={steps(isBunkerBarge)[currentStepIndex - 1]?.label}
                    nextLabelTranslationKey={steps(isBunkerBarge)[currentStepIndex + 1]?.label}
                  />
                </div>
              </div>
            </StepperTabPanel>
          ))}
        </StepperTabs>
        <div className="py-6 border-t border-t-[var(--stroke-secondary-stroke,#E6EDF2)]">
          <StepperControls
            cancel={cancel}
            complete={complete}
            completePending={submitPending}
            save={shouldShowSaveDraft ? handleSave : undefined}
            savePending={savePending}
            completeLabelTranslationKey="stepper-controls.submit"
          />
        </div>
      </div>
      {nameCheckDialogOpen && (
        <VesselsByNameDialog
          vessels={vessels}
          onClose={cancel}
          onConfirm={() => {
            setNameCheckDialogOpen(false);
            setPageNumber(1);
            setPageSize(10);
          }}
          isLoading={isLoading}
          pageNumber={pageNumber}
          onPageChange={setPageNumber}
          onPageSizeChange={setPageSize}
          pageSize={pageSize}
          totalRecordCount={totalRecordCount}
        />
      )}
    </div>
  );
};

export default VesselRegistrationContainer;
